import { withdrawApis } from "@payping/api";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { Badge, Col, Descriptions, Drawer, Row, Skeleton, usePaypingContext } from "@payping/web-components";
import { Dispatch, FC, SetStateAction } from "react";
import { ensureString } from "@payping/utilities-shared/string";
import { useAsyncRetry } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { getCorrectDate } from "@/utils/getCorrectDate";
import { useWithdrawStatus } from "../Withdraw/_hooks";

interface Props {
  code: string;
  setCode: Dispatch<SetStateAction<string | undefined>>;
}

export const WithdrawDetails: FC<Props> = ({ code, setCode }) => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const { loading, value: details } = useAsyncRetry(() => withdrawApis.withdrawDetails(code), [code]);
  const { statusText } = useWithdrawStatus();
  console.log({ details });
  const { text, color } = statusText((details as any)?.status);

  const { string: reqDateFa } = toJalali({
    date: getCorrectDate(details?.reqDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });
  const { string: repayDateFa } = toJalali({
    date: getCorrectDate(details?.repayDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });
  const trackingNumber = details?.isPardakhtYar ? details?.trackingNumber : details?.payareferencCode;
  const settlementType = details?.isPardakhtYar
    ? t("report.withdraws.details.isPardakhtyar")
    : t("report.withdraws.details.isNotPardakhtyar");

  return (
    <Drawer
      title={t("report.withdraws.details.title")}
      placement={md ? "left" : "bottom"}
      open={Boolean(code)}
      onClose={() => setCode(undefined)}
      size={md ? undefined : "100%"}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.withdraws.details.title")}>
              <Descriptions.Item label={t("withdraw.status.title")}>
                <Badge status={color} text={text} />
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.requestDate")}>
                {ensureString({ text: reqDateFa ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.repayDate")}>
                {ensureString({ text: repayDateFa ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.trackingNumber")}>
                {ensureString({ text: trackingNumber ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.shaba")}>
                {ensureString({ text: details?.shaba ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.settlementType")}>
                {ensureString({ text: settlementType ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              {details?.isPardakhtYar ? (
                <Descriptions.Item label={t("report.withdraws.details.gateway")}>
                  {ensureString({ text: details?.ipgType ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t("report.withdraws.details.code")}>
                {ensureString({ text: code ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              {details?.description ? (
                <Descriptions.Item label={t("report.withdraws.details.description")}>
                  {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          )}
        </Col>
      </Row>
    </Drawer>
  );
};

export default WithdrawDetails;
